import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { findNode, pathTo, searchTree, treeIds } from "@/utils/tree";

/**
 * Search, expansion and selection for a `Tree`, kept in `?node=` and `?open=`
 * so a reload or a back navigation restores it.
 *
 * @param {import("vue").Ref<Array>} items - the full tree
 * @param {object} [options]
 * @param {() => boolean} [options.writable] - when the url may be written to,
 *   for a tree that is not always on screen
 */
export function useTreeUrlState(items, { writable = () => true } = {}) {
  const route = useRoute();
  const router = useRouter();

  const query = ref("");
  const matched = computed(() => {
    const needle = query.value.trim().toLowerCase();
    return needle ? searchTree(items.value, needle) : null;
  });
  const visibleItems = computed(() => matched.value?.items ?? items.value);
  // Shape only: a node's own contents can change - a badge appearing after a
  // check - and that must not read as the tree being repopulated
  const structure = computed(() => treeIds(items.value).join(","));

  const selected = ref(null);
  const selectedId = ref(null);
  const expandIds = ref([]);

  let restoredNode = null;
  let restoredOpen = null;

  function readUrl() {
    restoredNode = typeof route.query.node === "string" ? route.query.node : null;
    restoredOpen = typeof route.query.open === "string" ? route.query.open.split(",") : null;
  }
  readUrl();

  function select(node) {
    selected.value = node.entity;
    selectedId.value = node.id;
  }

  function reset() {
    query.value = "";
    selected.value = null;
    selectedId.value = null;
    expandIds.value = [];
  }

  // Call once the tree holds the nodes the url names
  function restoreSelection() {
    const node = findNode(items.value, restoredNode);
    restoredNode = null;
    if (node?.entity) select(node);
  }

  watch([matched, structure], () => {
    // Kept until a search ends it, not consumed on first use: the tree is
    // populated over several assignments, each of which recomputes this
    if (matched.value) restoredOpen = null;
    const base = matched.value?.expand ?? restoredOpen ?? items.value.map((item) => item.id);
    expandIds.value = [
      ...new Set([...base, ...(pathTo(visibleItems.value, selectedId.value) ?? [])]),
    ];
  });

  // `replace`, so expanding a row is not a history entry of its own
  watch([selectedId, expandIds], () => {
    if (!writable()) return;
    void router.replace({
      query: {
        ...route.query,
        node: selectedId.value ?? undefined,
        open: expandIds.value.join(",") || undefined,
      },
    });
  });

  return {
    query,
    visibleItems,
    selected,
    selectedId,
    expandIds,
    select,
    reset,
    readUrl,
    restoreSelection,
  };
}
